import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ExternalLinkIcon, BarChart3Icon, UsersIcon, TrendingUpIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function CaseStudiesPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-705">
      <SEOHead title="Case Studies | Real Results for Local Businesses | Viridia Analytics" description="See how Viridia Analytics helps home service businesses in Phoenix and beyond capture more leads, automate follow-up, and book more jobs." canonical="/case-studies" keywords="marketing case studies, HVAC marketing results, plumber marketing success, local business growth" data-id="element-706" />

      {/* HERO SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-707">
        <ScrollReveal data-id="element-708">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-709">
            PROVEN RESULTS
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-710">
            Real Growth for{' '}
            <span className="text-viridia" data-id="element-711">Real Businesses</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-712">
            We don't just talk about leads and AI conversion. We build systems
            that actually generate revenue for local service businesses.
          </p>
        </ScrollReveal>
      </section>

      {/* FEATURED CASE STUDY */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-713">
        <ScrollReveal delay={0.1} data-id="element-714">
          <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl flex flex-col lg:flex-row" data-id="element-715">
            <div className="lg:w-1/2 bg-gray-900 p-8 lg:p-12 flex flex-col justify-center relative overflow-hidden" data-id="element-716">
              <div className="absolute top-0 right-0 w-64 h-64 bg-viridia/10 rounded-full blur-[80px]" data-id="element-717"></div>
              <div className="relative z-10" data-id="element-718">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold tracking-wide uppercase mb-6 border border-blue-500/20" data-id="element-719">
                  Featured Success Story
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-720">
                  How We Helped TFP Landscaping Nearly Double Their Competitor's
                  Conversion Rate
                </h2>
                <p className="text-gray-400 mb-8 text-lg" data-id="element-721">
                  TFP Landscaping is a Phoenix-based landscaper — one guy
                  running multiple crews with no office staff and no in-house
                  marketing system. Yelp ad spend was being wasted due to 4+
                  hour response times. See how our AI responder is now booking
                  leads he would normally miss.
                </p>
                <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-8" data-id="element-722">
                  <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-723">
                    <p className="text-2xl sm:text-3xl font-bold text-viridia mb-1" data-id="element-724">
                      39.75%
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-725">
                      Conversion Rate
                    </p>
                    <p className="text-gray-500 text-xs mt-1" data-id="element-726">
                      vs 22.3% industry avg
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-727">
                    <p className="text-2xl sm:text-3xl font-bold text-viridia mb-1" data-id="element-728">
                      $41.87
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-729">
                      Cost Per Lead
                    </p>
                    <p className="text-gray-500 text-xs mt-1" data-id="element-730">
                      19% lower than top competitor
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-731">
                    <p className="text-2xl sm:text-3xl font-bold text-viridia mb-1" data-id="element-732">
                      120+
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-733">
                      Leads Generated
                    </p>
                    <p className="text-gray-500 text-xs mt-1" data-id="element-734">
                      Last 30 days (as of 01/26)
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-735">
                    <p className="text-2xl sm:text-3xl font-bold text-viridia mb-1" data-id="element-736">
                      6 Mos
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-737">
                      Partnership
                    </p>
                    <p className="text-gray-500 text-xs mt-1" data-id="element-738">
                      Ongoing optimization
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2 bg-viridia-black p-8 lg:p-12 border-l border-gray-800 flex flex-col justify-center" data-id="element-739">
              <div className="space-y-8 mb-8" data-id="element-740">
                <div className="flex items-start gap-4" data-id="element-741">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0" data-id="element-742">
                    <TrendingUpIcon className="w-6 h-6 text-red-500" data-id="element-743" />
                  </div>
                  <div data-id="element-744">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-745">
                      The Challenge
                    </h3>
                    <p className="text-gray-400" data-id="element-746">
                      1 guy running multiple crews with no office staff or
                      in-house marketing system. Yelp ad spend was being wasted
                      because he couldn't reply to messages while on the job,
                      resulting in 4+ hour response times and lost leads.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-747">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0" data-id="element-748">
                    <BarChart3Icon className="w-6 h-6 text-blue-500" data-id="element-749" />
                  </div>
                  <div data-id="element-750">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-751">
                      The Solution
                    </h3>
                    <p className="text-gray-400" data-id="element-752">
                      Implemented our AI Conversational Chatbot to instantly
                      respond to Yelp inquiries, qualify leads, and
                      automatically book estimates while he's out in the field.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-753">
                  <div className="w-12 h-12 rounded-xl bg-viridia/10 flex items-center justify-center flex-shrink-0" data-id="element-754">
                    <UsersIcon className="w-6 h-6 text-viridia" data-id="element-755" />
                  </div>
                  <div data-id="element-756">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-757">
                      The Result
                    </h3>
                    <p className="text-gray-400" data-id="element-758">
                      The AI responder is now booking leads he would normally
                      miss. Conversion rates nearly doubled the industry
                      average, and cost per lead dropped by 19%.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-viridia-surface p-6 rounded-xl border border-gray-800 relative" data-id="element-759">
                <div className="text-viridia text-5xl absolute top-2 left-4 opacity-20 font-serif leading-none" data-id="element-760">
                  "
                </div>
                <p className="text-gray-300 italic relative z-10 pl-6 mb-4" data-id="element-761">
                  I'm getting more jobs with the same ad spend. The auto
                  response system is booking me almost 10 new estimates per
                  week, and almost half of them become clients.
                </p>
                <p className="text-white font-bold pl-6 text-sm" data-id="element-762">
                  — Phoenix Landscaping Company Owner
                </p>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* UPCOMING CASE STUDIES (PLACEHOLDERS) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-763">
        <ScrollReveal data-id="element-764">
          <div className="mb-12" data-id="element-765">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-766">
              More Success Stories Coming Soon
            </h2>
            <p className="text-gray-400" data-id="element-767">
              We are currently compiling data for our latest client wins.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-768">
          {/* Placeholder 1 */}
          <ScrollReveal delay={0.1} data-id="element-769">
            <div className="bg-viridia-surface/50 border border-gray-800 border-dashed rounded-2xl p-8 h-full flex flex-col justify-center items-center text-center relative overflow-hidden" data-id="element-770">
              <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center p-6" data-id="element-771">
                <span className="bg-gray-800 text-gray-300 text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider" data-id="element-772">
                  Data Collection in Progress
                </span>
                <p className="text-sm text-gray-400 max-w-xs" data-id="element-773">
                  [Need exact lead or revenue increase metric, client industry,
                  city, and approved dashboard screenshot from Kevin]
                </p>
              </div>
              <div className="opacity-20 blur-sm" data-id="element-774">
                <div className="w-16 h-16 bg-gray-700 rounded-full mx-auto mb-4" data-id="element-775"></div>
                <div className="h-6 w-48 bg-gray-700 rounded mx-auto mb-4" data-id="element-776"></div>
                <div className="h-4 w-full bg-gray-700 rounded mb-2" data-id="element-777"></div>
                <div className="h-4 w-3/4 bg-gray-700 rounded mx-auto" data-id="element-778"></div>
              </div>
            </div>
          </ScrollReveal>

          {/* Placeholder 2 */}
          <ScrollReveal delay={0.2} data-id="element-779">
            <div className="bg-viridia-surface/50 border border-gray-800 border-dashed rounded-2xl p-8 h-full flex flex-col justify-center items-center text-center relative overflow-hidden" data-id="element-780">
              <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center p-6" data-id="element-781">
                <span className="bg-gray-800 text-gray-300 text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider" data-id="element-782">
                  Pending Client Approval
                </span>
                <p className="text-sm text-gray-400 max-w-xs" data-id="element-783">
                  [Need client permission to name business, before/after
                  details, and testimonial quote from Kevin]
                </p>
              </div>
              <div className="opacity-20 blur-sm" data-id="element-784">
                <div className="w-16 h-16 bg-gray-700 rounded-full mx-auto mb-4" data-id="element-785"></div>
                <div className="h-6 w-48 bg-gray-700 rounded mx-auto mb-4" data-id="element-786"></div>
                <div className="h-4 w-full bg-gray-700 rounded mb-2" data-id="element-787"></div>
                <div className="h-4 w-3/4 bg-gray-700 rounded mx-auto" data-id="element-788"></div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden mt-12" data-id="element-789">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-790"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-791">
          <ScrollReveal data-id="element-792">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-793">
              Want to Be Our Next Case Study?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-794">
              Book a free strategy call and let's build a system that captures
              leads, responds faster, and books more jobs for your business.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-795">
              Book Your Free Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-796" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}